import { useState, useCallback, useMemo, useEffect } from "react";
import { ListPager } from "@/components/data-grid/ListPager";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Plus,
  Mail,
  Phone,
  Calendar,
  Star,
  AlertTriangle,
  FileText,
  Building2,
  TrendingUp,
  ExternalLink,
  Trash2,
  Edit,
  Download,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { AddBuyerDrawer } from "@/components/add/AddBuyerDrawer";
import { OnePagerImportModal } from "@/components/import/OnePagerImportModal";
import { ImportDialog } from "@/components/import/ImportDialog";
import { BuyerCard, BuyerCardSkeleton } from "@/components/BuyerCard";
import {
  AirtableGrid,
  GridToolbar,
  Column,
  ViewMode,
} from "@/components/data-grid";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmptyState } from "@/components/EmptyState";
import { MobileBuyerCard, MobileBuyerCardSkeleton } from "@/components/mobile";
import { ListShell } from "@/components/list/ListShell";
import { ListTiles } from "@/components/list/ListTiles";
import { ChartChips, ALL_KEY } from "@/components/list/ChartChips";
import { buyerTiles, buyerChips, type BuyerStats } from "@/components/list/tiles/buyerTiles";
import { formatDealValue } from "@shared/formatDealValue";
import { UNCLASSIFIED_FIRM_TYPE } from "@shared/buyerFirmTypes";

interface Buyer {
  id: string;
  name: string;
  category?: string | null;
  initials: string | null;
  subType: string | null;
  industry: string | null;
  targetIndustries: string[] | null;
  targetEV: string | null;
  contactCount: number;
  lastContactName: string | null;
  website: string | null;
  notes: string | null;
  createdAt: string;
  primaryContactEmail?: string | null;
  primaryContactPhone?: string | null;
  enrichmentData?: { source?: string; [key: string]: any } | null;
  /** Derived server-side: any call, meeting or conference logged for this firm. */
  engaged?: boolean;
  minDealSize: string | null;
  maxDealSize: string | null;
  minEbitda: string | null;
  openTrackers: number;
  furthestStage: string | null;
  lastActivityAt: string | null;
}

const formatTimeAgo = (iso: string) => {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Canonical buyer firm types per shared/schema.ts + CLAUDE.md:
// Private Equity | Strategic Acquirer | Independent Sponsor | Family Office | Search Fund | Other
const getSubTypeBadgeStyle = (subType: string) => {
  switch (subType) {
    case "Private Equity":
      return "bg-primary text-primary-foreground";
    case "Strategic Acquirer":
      return "bg-slate-600 text-white";
    case "Independent Sponsor":
      return "bg-teal-600 text-white";
    case "Family Office":
      return "bg-purple-600 text-white";
    case "Search Fund":
      return "bg-amber-600 text-white";
    case "Other":
      return "bg-zinc-500 text-white";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const getTierConfig = (tier: number) => {
  switch (tier) {
    case 1:
      return {
        label: "Tier 1",
        className: "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900",
        icon: <Star className="w-3 h-3" />,
      };
    case 2:
      return {
        label: "Tier 2",
        className: "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-900",
        icon: null,
      };
    case 3:
      return {
        label: "Tier 3",
        className: "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-900/30 dark:border-gray-900",
        icon: null,
      };
    default:
      return {
        label: "Tier 3",
        className: "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-900/30 dark:border-gray-900",
        icon: null,
      };
  }
};

const getActivityIcon = (type: string) => {
  switch (type) {
    case "email":
      return Mail;
    case "call":
      return Phone;
    case "meeting":
      return Calendar;
    default:
      return Mail;
  }
};

const isOverdue = (activity: string) => {
  if (activity.includes("week") || activity.includes("month")) return true;
  const dayMatch = activity.match(/(\d+)\s*days?/);
  if (dayMatch) {
    const days = parseInt(dayMatch[1]);
    return days > 30;
  }
  return false;
};

export default function BuyerNetwork() {
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [quickFilter, setQuickFilter] = useState<string>(ALL_KEY);
  const [industryFilter, setIndustryFilter] = useState("All");
  const [engagedFilter, setEngagedFilter] = useState("All");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [selectedBuyers, setSelectedBuyers] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [page, setPage] = useState(1);
  // Per-page sizes; "all" lets power users see everything (capped to 2000
  // server-side). Default 50 matches what the user originally asked for.
  const [pageSize, setPageSize] = useState<number | "all">(50);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Any filter change resets pagination back to page 1.
  useEffect(() => {
    setPage(1);
    setSelectAllMatching(false);
  }, [debouncedSearch, quickFilter, industryFilter, engagedFilter, pageSize]);

  interface BuyersResponse {
    data: Buyer[];
    pagination: { page: number; limit: number; total: number; totalPages: number; hasMore: boolean };
    stats: BuyerStats;
  }

  const buyerFirmTypeQueryValue = useMemo(() => {
    switch (quickFilter) {
      case "pe": return "Private Equity";
      case "strategic": return "Strategic Acquirer";
      case "indepSponsor": return "Independent Sponsor";
      case "familyOffice": return "Family Office";
      case "searchFund": return "Search Fund";
      case "other": return "Other";
      case "unclassified": return UNCLASSIFIED_FIRM_TYPE;
      default: return null;
    }
  }, [quickFilter]);

  const buyersQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (industryFilter && industryFilter !== "All") params.set("industry", industryFilter);
    if (buyerFirmTypeQueryValue) params.set("firmType", buyerFirmTypeQueryValue);
    if (engagedFilter === "Spoken to") params.set("engaged", "yes");
    if (engagedFilter === "Never contacted") params.set("engaged", "no");
    if (pageSize === "all") {
      params.set("limit", "all");
    } else {
      params.set("page", String(page));
      params.set("limit", String(pageSize));
    }
    return params.toString();
  }, [debouncedSearch, industryFilter, buyerFirmTypeQueryValue, engagedFilter, page, pageSize]);

  const { data: buyersResponse, isLoading, isFetching, refetch } = useQuery<BuyersResponse>({
    queryKey: ["buyers", buyersQueryParams],
    queryFn: async () => {
      const response = await fetch(`/api/buyers?${buyersQueryParams}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch buyers");
      return response.json();
    },
    placeholderData: (prev) => prev,
  });

  const buyers = buyersResponse?.data ?? [];
  const totalMatching = buyersResponse?.pagination.total ?? 0;
  const hasNextPage = buyersResponse?.pagination.hasMore ?? false;
  const hasPrevPage = page > 1 && pageSize !== "all";

  // Export hits the server endpoint which streams ALL matching buyers
  // (respecting search/industry/firmType) regardless of pagination. We
  // open it in a new tab so the browser handles the download natively
  // and the auth cookie travels with the request.
  const exportToCSV = () => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (industryFilter && industryFilter !== "All") params.set("industry", industryFilter);
    if (buyerFirmTypeQueryValue) params.set("firmType", buyerFirmTypeQueryValue);
    const url = `/api/buyers/export${params.toString() ? `?${params}` : ""}`;
    window.location.href = url;
    toast.success(`Exporting ${totalMatching} buyers...`);
  };

  const deleteBuyerMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/companies/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete buyer");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["buyers"] });
      toast.success("Buyer deleted");
    },
    onError: () => toast.error("Failed to delete buyer"),
  });

  // Counts come from the server's stats payload, which is computed
  // within the current search+industry filter but ignoring firmType.
  // That keeps each firm-type badge meaningful when one is selected.
  const counts: BuyerStats = buyersResponse?.stats ?? {
    all: 0, pe: 0, strategic: 0, indepSponsor: 0, familyOffice: 0, searchFund: 0, other: 0, engaged: 0,
    unclassified: 0, onTracker: 0, withCriteria: 0,
  };

  const money = (v: string | null) => (v ? formatDealValue(v) : null);
  const dealSize = (b: Buyer) => {
    const mn = money(b.minDealSize), mx = money(b.maxDealSize);
    return mn && mx ? (mn === mx ? mn : `${mn} to ${mx}`) : mn ? `${mn}+` : mx ? `up to ${mx}` : null;
  };
  const dim = <span className="text-muted-foreground/50">—</span>;
  const columns: Column<Buyer>[] = useMemo(() => [
    { id: "name", header: "Company", width: "w-[240px]", cell: (b) => (
      <span className="flex items-center gap-1.5 min-w-0" title={b.name}>
        <span className="font-medium truncate">{b.name}</span>
        {b.enrichmentData?.source && <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" title="Enriched" />}
        {b.category === "Capital Providers" && <span className="shrink-0 text-[10px] text-muted-foreground bg-muted px-1 rounded">Also CP</span>}
      </span>
    ) },
    { id: "firmType", header: "Type", width: "w-[150px]", cell: (b) => b.subType
      ? <Badge className={cn("text-[11px] px-1.5 py-0 whitespace-nowrap", getSubTypeBadgeStyle(b.subType))}>{b.subType}</Badge>
      : <Badge variant="outline" className="text-[11px] px-1.5 py-0 whitespace-nowrap text-muted-foreground border-dashed">Unclassified</Badge> },
    { id: "focus", header: "Focus", cell: (b) => {
      const d = b.industry || (b.targetIndustries?.length ? b.targetIndustries.slice(0, 2).join(", ") : null) || b.website || null;
      return d ? <span className="text-muted-foreground" title={d}>{d}</span> : dim;
    } },
    { id: "dealSize", header: "Deal size", width: "w-[120px]", cell: (b) => { const s = dealSize(b); return s ? <span className="tabular-nums" title={s}>{s}</span> : dim; } },
    { id: "minEbitda", header: "Min EBITDA", width: "w-[100px]", className: "text-right", headerClassName: "text-right",
      cell: (b) => (b.minEbitda ? <span className="tabular-nums">{formatDealValue(b.minEbitda)}</span> : dim) },
    { id: "contacts", header: "Contacts", width: "w-[78px]", className: "hidden 2xl:table-cell text-center", headerClassName: "hidden 2xl:table-cell text-center",
      cell: (b) => <span className="tabular-nums">{b.contactCount}</span> },
    { id: "engaged", header: "Spoken to", width: "w-[80px]", className: "text-center", headerClassName: "text-center",
      cell: (b) => (b.engaged ? <CheckCircle2 className="inline w-4 h-4 text-emerald-600 dark:text-emerald-400" aria-label="Spoken to" /> : dim) },
    { id: "inProcess", header: "In process", width: "w-[112px]", cell: (b) => (b.openTrackers > 0
      ? <span title={`${b.openTrackers} open ${b.openTrackers === 1 ? "tracker" : "trackers"}${b.furthestStage ? `, furthest: ${b.furthestStage}` : ""}`}>
          <span className="tabular-nums">{b.openTrackers}</span>
          {b.furthestStage && <Badge variant="outline" className="ml-1.5 text-[10px] px-1 py-0">{b.furthestStage}</Badge>}
        </span>
      : dim) },
    { id: "lastActivity", header: "Last activity", width: "w-[104px]", className: "text-right", headerClassName: "text-right",
      cell: (b) => (b.lastActivityAt ? <span className="text-muted-foreground">{formatTimeAgo(b.lastActivityAt)}</span> : dim) },
  ], []);

  // Selects/deselects just the visible page. The "Select all N matches"
  // banner below is what extends the selection beyond the page.
  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedBuyers(new Set(buyers.map((b) => b.id)));
    } else {
      setSelectedBuyers(new Set());
      setSelectAllMatching(false);
    }
  };

  // Banner-driven select-all: fetches every matching buyer ID under the
  // current filters and selects them. Bounded by server limit=all cap (2000).
  const selectAllAcrossPages = async () => {
    const params = new URLSearchParams(buyersQueryParams);
    params.set("limit", "all");
    params.delete("page");
    const res = await fetch(`/api/buyers?${params}`, { credentials: "include" });
    if (!res.ok) return toast.error("Couldn't select all matches");
    const body = await res.json();
    setSelectedBuyers(new Set((body.data ?? []).map((b: Buyer) => b.id)));
    setSelectAllMatching(true);
    toast.success(`Selected ${body.data?.length ?? 0} buyers`);
  };

  const toggleSelect = (id: string | number, checked: boolean) => {
    const strId = String(id);
    const newSelected = new Set(selectedBuyers);
    if (checked) {
      newSelected.add(strId);
    } else {
      newSelected.delete(strId);
    }
    setSelectedBuyers(newSelected);
  };

  const handleRowClick = (buyer: Buyer) => {
    setLocation(`/buyers/${buyer.id}`);
  };

  const getRowActions = (buyer: Buyer) => [
    { label: "View Details", icon: <ExternalLink className="w-4 h-4" />, onClick: () => setLocation(`/buyers/${buyer.id}`) },
    { label: "Edit", icon: <Edit className="w-4 h-4" />, onClick: () => setLocation(`/buyers/${buyer.id}`) },
    { label: "Delete", icon: <Trash2 className="w-4 h-4" />, onClick: () => setPendingDelete({ id: buyer.id, name: buyer.name }), variant: "destructive" as const },
  ];

  // Canonical target-industry ENUM used by the enrichment prompt in
  // server/routes.ts. Keep this list in sync — adding an industry to
  // the prompt means adding it here so users can filter on it.
  const filters = [
    {
      id: "industry",
      label: "Industry",
      options: [
        { value: "All", label: "All Industries" },
        { value: "Business Services", label: "Business Services" },
        { value: "Construction & Infrastructure", label: "Construction & Infrastructure" },
        { value: "Consumer", label: "Consumer" },
        { value: "Energy", label: "Energy" },
        { value: "Financial Services", label: "Financial Services" },
        { value: "Food & Beverage", label: "Food & Beverage" },
        { value: "Healthcare", label: "Healthcare" },
        { value: "Industrial", label: "Industrial" },
        { value: "Manufacturing", label: "Manufacturing" },
        { value: "SaaS & Technology", label: "SaaS & Technology" },
        { value: "Technology", label: "Technology" },
        { value: "Transportation & Logistics", label: "Transportation & Logistics" },
        { value: "Other", label: "Other" },
      ],
      value: industryFilter,
      onChange: setIndustryFilter,
      defaultValue: "All",
    },
    {
      id: "engaged",
      label: "Contact",
      options: [
        { value: "All", label: "All" },
        { value: "Spoken to", label: "Spoken to" },
        { value: "Never contacted", label: "Never contacted" },
      ],
      value: engagedFilter,
      onChange: setEngagedFilter,
      defaultValue: "All",
    },
  ];

  const CardView = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <BuyerCardSkeleton key={i} />
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {buyers.map((buyer) => (
          <BuyerCard
            key={buyer.id}
            buyer={buyer as any}
            onClick={() => handleRowClick(buyer)}
          />
        ))}
      </div>
    );
  };

  const tiles = buyerTiles(counts).map((t) => t.key === "engaged"
    ? { ...t, active: engagedFilter === "Spoken to", onClick: () => setEngagedFilter(engagedFilter === "Spoken to" ? "All" : "Spoken to") }
    : t);

  return (
    <ListShell
      title="Buyer Network"
      tiles={<ListTiles tiles={tiles} />}
      toolbar={
        <GridToolbar
          density="compact"
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search buyers..."
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          filters={filters}
          chips={<ChartChips ariaLabel="Firm type" chips={buyerChips(counts)} allCount={counts.all} value={quickFilter} onChange={setQuickFilter} />}
          primaryAction={
            <Button
              onClick={() => setDrawerOpen(true)}
              size="sm"
              className="h-8 gap-2 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              data-testid="add-buyer-button"
            >
              <Plus className="w-4 h-4" />
              Add Buyer
            </Button>
          }
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={exportToCSV}
                className="h-8 gap-2"
                data-testid="export-buyers-button"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setImportModalOpen(true)}
                    className="h-8 gap-2"
                    data-testid="import-one-pager-button"
                  >
                    <FileText className="w-4 h-4" />
                    Import One-Pager
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Import buyer from PDF or image</p>
                </TooltipContent>
              </Tooltip>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkImportOpen(true)}
                className="h-8 gap-2"
              >
                <Download className="w-4 h-4" />
                Import Excel
              </Button>
            </>
          }
        />
      }
      above={
        /* Select-all-across-pages banner, Gmail style. Only shows when the
           visible page is fully selected and there are more matches beyond it. */
        selectedBuyers.size > 0 && selectedBuyers.size >= buyers.length && totalMatching > buyers.length ? (
          <div className="flex items-center justify-center gap-3 bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-sm dark:bg-amber-950/40 dark:border-amber-900">
            {selectAllMatching ? (
              <>
                <span>All {selectedBuyers.size} matching buyers are selected.</span>
                <button
                  className="font-medium text-primary-text hover:underline"
                  onClick={() => { setSelectedBuyers(new Set()); setSelectAllMatching(false); }}
                >
                  Clear selection
                </button>
              </>
            ) : (
              <>
                <span>{selectedBuyers.size} buyers on this page selected.</span>
                <button
                  className="font-medium text-primary-text hover:underline"
                  onClick={selectAllAcrossPages}
                >
                  Select all {totalMatching} matches
                </button>
              </>
            )}
          </div>
        ) : null
      }
      footer={
        <ListPager
          density="compact"
          shown={buyers.length} total={totalMatching} noun={["buyer", "buyers"]}
          page={page} totalPages={buyersResponse?.pagination.totalPages || 1}
          pageSize={pageSize} onPageSizeChange={setPageSize}
          hasPrev={hasPrevPage} hasNext={hasNextPage}
          onPrev={() => setPage((p) => Math.max(1, p - 1))} onNext={() => setPage((p) => p + 1)}
          busy={isFetching} updating={isFetching && !isLoading} testIdPrefix="buyers"
        />
      }
      after={
        <>
          <AddBuyerDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
          <OnePagerImportModal open={importModalOpen} onOpenChange={setImportModalOpen} />
          <ImportDialog type="buyers" open={bulkImportOpen} onOpenChange={setBulkImportOpen} />
          <ConfirmDialog
            open={!!pendingDelete}
            onOpenChange={(next) => !next && !deleteBuyerMutation.isPending && setPendingDelete(null)}
            title={pendingDelete ? `Delete ${pendingDelete.name}?` : ""}
            description="This archives the buyer so it no longer shows in the active list. You can restore it anytime from the Archived page in Settings."
            confirmLabel="Delete"
            cancelLabel="Cancel"
            variant="destructive"
            isLoading={deleteBuyerMutation.isPending}
            onConfirm={async () => {
              if (!pendingDelete) return;
              await deleteBuyerMutation.mutateAsync(pendingDelete.id);
              setPendingDelete(null);
            }}
          />
        </>
      }
    >
      {/* Desktop Table View */}
      <div className="hidden md:flex md:flex-col lg:flex-1 lg:min-h-0">
        {viewMode === "table" ? (
          <AirtableGrid
            density="compact"
            scroll
            data={buyers}
            columns={columns}
            onRowClick={handleRowClick}
            getRowActions={getRowActions}
            selectedRows={selectedBuyers}
            onSelectRow={toggleSelect}
            onSelectAll={toggleSelectAll}
            getRowId={(buyer) => buyer.id}
            loading={isLoading}
            emptyState={
              <EmptyState
                type="buyers"
                onAction={() => setDrawerOpen(true)}
              />
            }
          />
        ) : (
          <div className="lg:flex-1 lg:min-h-0 lg:overflow-auto p-3"><CardView /></div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3 p-3">
        {isLoading ? (
          <>
            <MobileBuyerCardSkeleton />
            <MobileBuyerCardSkeleton />
            <MobileBuyerCardSkeleton />
          </>
        ) : buyers.length === 0 ? (
          <EmptyState
            type="buyers"
            onAction={() => setDrawerOpen(true)}
          />
        ) : (
          buyers.map((buyer) => (
            <MobileBuyerCard
              key={buyer.id}
              buyer={{
                id: buyer.id,
                name: buyer.name,
                subType: buyer.subType,
                industry: buyer.industry,
                primaryContactEmail: buyer.primaryContactEmail,
                primaryContactPhone: buyer.primaryContactPhone,
              }}
            />
          ))
        )}
      </div>
    </ListShell>
  );
}
